import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import RHAudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';

import { MUSIC_LIBRARY_VARIABLES } from './constants';
import PlayIcon from './assets/playIcon.inline.svg';
import PauseIcon from './assets/pauseIcon.inline.svg';
import { Track } from './types';

interface IAudioPlayer {
  track: Track['track'];
  innerRef: React.Ref<RHAudioPlayer>;
  onPlay: () => void;
}

const StyledAudioPlayer = styled(RHAudioPlayer)`
  ${({ theme: { mq } }) => css`
    &.rhap_container {
      box-shadow: initial;
      background-color: transparent;
      padding: initial;
      min-width: 62.125rem;
    }

    .rhap_ {
      &play-pause-button {
        display: flex;
        margin: 0;
        width: 3rem;
        height: 3rem;

        & > svg {
          width: 100%;
          height: 100%;

          & > * {
            stroke: var(${MUSIC_LIBRARY_VARIABLES.iconColor});
          }
        }
      }

      &progress-container {
        margin: 0;
        height: 100%;
      }

      &progress-section {
        column-gap: 6rem;
      }

      &progress-bar {
        margin-left: 2rem;
        background-color: transparent;
        height: 100%;

        &::before {
          display: block;
          position: absolute;
          top: 50%;
          left: -2rem;
          transform: translateY(-50%);
          z-index: 2;
          border-radius: 0.25rem 0 0 0.25rem;
          background-color: var(${MUSIC_LIBRARY_VARIABLES.accentColor});
          width: 2rem;
          height: 0.25rem;
          content: '';
        }

        &::after {
          display: block;
          position: absolute;
          top: 50%;
          left: 0;
          transform: translateY(-50%);
          z-index: 2;
          border-radius: 0.25rem;
          background-color: var(${MUSIC_LIBRARY_VARIABLES.progressBarColor});
          width: 100%;
          height: 0.25rem;
          content: '';
        }
      }

      &progress-indicator {
        top: 50%;
        transform: translateY(-50%);
        margin-left: -2rem;
        border: 0.25rem solid var(${MUSIC_LIBRARY_VARIABLES.accentColor});
        border-radius: 50%;
        background-color: var(${MUSIC_LIBRARY_VARIABLES.indicatorBackgroundColor});
        width: 2rem;
        height: 2rem;
      }

      &progress-filled {
        top: 50%;
        transform: translateY(-50%);
        border-radius: 0 0.25rem 0.25rem 0;
        background-color: var(${MUSIC_LIBRARY_VARIABLES.accentColor});
        height: 0.25rem;
      }

      &controls-section {
        display: none;
      }
    }

    ${mq.md} {
      &.rhap_container {
        width: 100%;
        min-width: initial;
      }

      .rhap_ {
        &play-pause-button {
          width: 2.25rem;
          height: 2.25rem;
        }

        &progress-section {
          column-gap: 2rem;
        }
      }
    }
  `}
`;

const AudioPlayer: React.FC<IAudioPlayer> = ({ track, innerRef, onPlay }) => {
  return (
    <StyledAudioPlayer
      src={track}
      ref={innerRef}
      autoPlay={false}
      layout="horizontal-reverse"
      customProgressBarSection={[RHAP_UI.MAIN_CONTROLS, RHAP_UI.PROGRESS_BAR]}
      customAdditionalControls={[]}
      customControlsSection={[]}
      customVolumeControls={[]}
      showJumpControls={false}
      showDownloadProgress={false}
      customIcons={{
        play: <PlayIcon />,
        pause: <PauseIcon />,
      }}
      onPlay={onPlay}
    />
  );
};

export default AudioPlayer;
